import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### メッセージ送信の返信 API 操作クラス
 */
export default class OtherUserGroupSendMessageReplies {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET SendMessageReplies
   */
  public async get(userStore: LoginUser, datetime: number): Promise<Array<SendMessageReplyModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupSendMessageReplies, formatParams);
    const response = await this.commonService.executeGet<Array<SendMessageReplyModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET SendMessageRepliesFacilityUnread
   */
  public async getUnreadInFacility(userStore: LoginUser): Promise<SendMessageRepliesUnreadModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupSendMessageRepliesUnreadInFacility, formatParams);
    const response = await this.commonService.executeGet<SendMessageRepliesUnreadModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * GET OtherUserGroupSendMessageRepliesUnread
   */
  public async getUnread(userStore: LoginUser, datetime: number): Promise<SendMessageRepliesUnreadModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserGroupSendMessageRepliesUnread, formatParams);
    const response = await this.commonService.executeGet<SendMessageRepliesUnreadModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.IGNORE, 0);

    return response.data;
  }

  /**
   * POST SendOtherUserGroupMessageReplies
   */
  public async post(userStore: LoginUser, parentMessagesSortKey: string, groupId: number, memberId: number, requestBody: OtherUserGroupSendMessageRepliesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      parentMessagesSortKey: parentMessagesSortKey,
      groupId: groupId,
      memberId: memberId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendMessageReplyToOtherUser, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

/**
 * ### メッセージ送信の返信
 */
export interface SendMessageReplyModel {
  /** 施設 ID */
  facilityId: number;
  /** タイムスタンプ（yyyymmddhhMMssSSS） */
  datetime: number;
  /** ソートキー */
  otherUserReplyMessagesSortKey: string;
  /** 返信先メッセージのソートキー */
  parentMessagesSortKey: string;
  /** 施設関係者グループ ID */
  groupId: number;
  /** 施設関係者グループメンバー ID */
  memberId: number;
  /** 返信内容のテキスト */
  message: string;
  /** 返信を読んだ時間リスト */
  readDatetimes: number[];
  /** 返信を読んだログインユーザー名リスト */
  readUserNames: string[];
  /** 返信を読んだログインユーザーIDリスト */
  readUserIds: string[];
  /** 施設関係者グループ名 */
  groupName: string;
  /** 施設関係者グループ種類 */
  groupTypeId: number;
  /** 施設関係者グループ種類名 */
  groupTypeName: string;
  /** 施設関係者グループメンバー名 */
  groupMemberName: string;
  /** 施設関係者グループメンバー電話番号 */
  groupMemberTel: string;
  /** 施設関係者グループメンバー所属 */
  groupMemberOrganization: string;
  /** レコード更新日 */
  updated: number;
  /** メッセージ送信の種別 */
  type: string;
  /** 送信種別(0:家族、1:施設) */
  senderType: SendMessageReplySenderType;
}

/**
 * ### メッセージ送信の種別
 */
export enum SendMessageReplyType {
  /** 返信 */
  REPLY = "reply",
  /** 送信エラー */
  ERROR = "error"
}

export enum SendMessageReplySenderType {
  OTHER_USER = 0,
  FACILITY = 1
}

/**
 * ### 施設のメッセージ送信の返信未読が存在するか否か
 */
export interface SendMessageRepliesUnreadModel {
  /** 未読の返信メッセージがあるか否か */
  unread: boolean;
}

export interface OtherUserGroupSendMessageRepliesPostModel {
  message: string;
}
